// client/src/components/styles/useStyles.js

import { useState, useEffect, useCallback } from "react";

// Usage: const { styles, loading, error, saveStyles, resetStyles, setDefault, refetch } = useStyles(mapId, user);

export default function useStyles(mapId, user) {
  // Initialize styles as empty object, never null
  const [styles, setStyles] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [defaultStyles, setDefaultStyles] = useState(null);

  // Fetch styles for the given mapId
  const fetchStyles = useCallback(() => {
    if (!mapId) return;
    setLoading(true);
    setError(null);
    fetch(`/api/map-styles/${mapId}`, { credentials: "include" })
      .then((res) => res.json())
      .then((data) => {
        setStyles(data || {});
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch styles.");
        setStyles({});
        setLoading(false);
      });
  }, [mapId]);

  // Fetch default styles (for "restore default" UI) – temporarily disabled while there
  // is no /api/map-styles/default endpoint in production.
  const fetchDefaultStyles = useCallback(() => {
    // If/when you add a real /api/map-styles/default endpoint on the backend,
    // replace this stub with a real fetch and setDefaultStyles(data || {}).
    setDefaultStyles(null);
  }, []);

  // Refetch styles on mapId change
  useEffect(() => {
    fetchStyles();
    fetchDefaultStyles();
  }, [fetchStyles, fetchDefaultStyles]);

  // Save/Update styles for this map
  const saveStyles = useCallback(
    async (fields) => {
      if (!mapId || !user) return;
      setSaving(true);
      setError(null);
      try {
        const res = await fetch(`/api/map-styles/${mapId}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(fields),
        });
        const data = await res.json();
        if (!res.ok || data.error) {
          setError(data.error || "Failed to save styles.");
        } else {
          setStyles(data || {});
        }
      } catch (e) {
        setError("Failed to save styles.");
      } finally {
        setSaving(false);
      }
    },
    [mapId, user]
  );

  // Reset to default for this map (deletes custom style row)
  const resetStyles = useCallback(async () => {
    if (!mapId || !user) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/map-styles/${mapId}/reset`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "Failed to reset styles.");
      } else {
        setStyles(data || {});
      }
    } catch (e) {
      setError("Failed to reset styles.");
    } finally {
      setSaving(false);
    }
  }, [mapId, user]);

  // Set current style as global default (admin/founder only)
  const setDefault = useCallback(async () => {
    if (!mapId || !user) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`/api/map-styles/${mapId}/set-default`, {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || "Failed to set as default.");
      } else {
        setStyles(data || {});
        fetchDefaultStyles(); // Update the cached default
      }
    } catch (e) {
      setError("Failed to set as default.");
    } finally {
      setSaving(false);
    }
  }, [mapId, user, fetchDefaultStyles]);

  // Refetch
  const refetch = fetchStyles;

  return {
    styles,
    loading,
    saving,
    error,
    defaultStyles,
    saveStyles,
    resetStyles,
    setDefault,
    refetch,
    setStyles, // You might use this for local previews before saving
  };
}
